/**
 * Bonus Calculator — determines Wouter's monthly bonus progress.
 *
 * Bonus tiers:
 *   Bronze (20 files) — R1,000
 *   Silver (25 files) — PAYE equivalent (configurable)
 *   Gold   (30 files) — Bronze + Silver combined
 */

const DEFAULT_MILESTONES = {
  bronze: { count: 20, amount: 1000, label: 'Bronze', emoji: '🥉' },
  silver: { count: 25, amount: 0, label: 'Silver', emoji: '🥈' },
  gold: { count: 30, amount: 0, label: 'Gold', emoji: '🥇' },
};

/**
 * Calculate bonus status for a given count of qualifying files.
 *
 * @param {number} qualifyingCount — number of bonus-qualifying files this month
 * @param {object} settings — optional overrides for milestones and PAYE amount
 * @returns {object} bonus status details
 */
export function calculateBonusStatus(qualifyingCount, settings = {}) {
  const milestones = {
    bronze: {
      ...DEFAULT_MILESTONES.bronze,
      count: settings.bronzeTarget || DEFAULT_MILESTONES.bronze.count,
      amount: settings.bronzeAmount ?? DEFAULT_MILESTONES.bronze.amount,
    },
    silver: {
      ...DEFAULT_MILESTONES.silver,
      count: settings.silverTarget || DEFAULT_MILESTONES.silver.count,
      amount: settings.silverAmount ?? DEFAULT_MILESTONES.silver.amount,
    },
    gold: {
      ...DEFAULT_MILESTONES.gold,
      count: settings.goldTarget || DEFAULT_MILESTONES.gold.count,
      amount: settings.goldAmount ?? DEFAULT_MILESTONES.gold.amount,
    },
  };

  // Determine current tier and next target
  let currentTier = null;
  let nextTier = milestones.bronze;

  if (qualifyingCount >= milestones.gold.count) {
    currentTier = milestones.gold;
    nextTier = null; // All tiers achieved
  } else if (qualifyingCount >= milestones.silver.count) {
    currentTier = milestones.silver;
    nextTier = milestones.gold;
  } else if (qualifyingCount >= milestones.bronze.count) {
    currentTier = milestones.bronze;
    nextTier = milestones.silver;
  }

  const progressToNext = nextTier
    ? Math.min(100, (qualifyingCount / nextTier.count) * 100)
    : 100;

  const filesUntilNext = nextTier ? Math.max(0, nextTier.count - qualifyingCount) : 0;

  return {
    qualifyingCount,
    currentTier,
    nextTier,
    progressToNext,
    filesUntilNext,
    milestones,
    totalEarned: calculateTotalEarned(qualifyingCount, milestones),
  };
}

/**
 * Calculate total bonus amount earned based on current count.
 */
function calculateTotalEarned(count, milestones) {
  if (count >= milestones.gold.count) return milestones.gold.amount;
  if (count >= milestones.silver.count) return milestones.silver.amount;
  if (count >= milestones.bronze.count) return milestones.bronze.amount;
  return 0;
}

/**
 * Format a Rand amount for display (e.g. "R1,000").
 */
export function formatRands(amount) {
  return `R${amount.toLocaleString('en-ZA')}`;
}
